import { useEffect, useState, useCallback, Fragment } from "react";
import { RefreshCw, CheckCircle2, AlertTriangle, Clock, PlugZap, ChevronRight, ChevronDown, Loader2, History, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { parseUtc } from "../lib/datetime";

const PLATFORM_LABEL = (p) => {
    const k = (p || "").toLowerCase();
    if (k === "meta") return "Meta";
    if (k === "google" || k === "dv360") return "DV360";
    if (k === "tiktok") return "TikTok";
    return k.toUpperCase() || "—";
};

const fmtInterval = (min) => {
    const m = Number(min) || 0;
    if (m <= 0) return "manual only";
    if (m % 1440 === 0) return `every ${m / 1440}d`;
    if (m % 60 === 0) return `every ${m / 60}h`;
    return `every ${m}m`;
};

const fmtWhen = (iso) => {
    if (!iso) return "Never";
    const d = parseUtc(iso);
    if (!d) return "Never";
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    let rel;
    if (mins < 1) rel = "just now";
    else if (mins < 60) rel = `${mins}m ago`;
    else if (mins < 1440) rel = `${Math.floor(mins / 60)}h ago`;
    else rel = `${Math.floor(mins / 1440)}d ago`;
    return `${rel} · ${d.toLocaleString("en-US")}`;
};

// Overdue = never synced, or last sync older than 2× the configured interval (a likely freeze).
const isOverdue = (c) => {
    const interval = Number(c.sync_interval_minutes) || 240;
    if (!c.last_synced_at) return true;
    const d = parseUtc(c.last_synced_at);
    if (!d) return true;
    const ageMin = (Date.now() - d.getTime()) / 60000;
    return ageMin > interval * 2;
};

const fmtDur = (s, f) => {
    if (!s) return "—";
    const start = parseUtc(s);
    if (!start) return "—";
    const end = f ? parseUtc(f) : new Date();
    const sec = Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000));
    if (sec < 60) return `${sec}s`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
};

const JobStatusBadge = ({ status }) => {
    const s = (status || "").toLowerCase();
    const map = {
        success: "bg-emerald-50 text-emerald-700 border-emerald-200",
        completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
        failed: "bg-red-50 text-red-700 border-red-200",
        running: "bg-blue-50 text-blue-700 border-blue-200",
        queued: "bg-amber-50 text-amber-700 border-amber-200",
    };
    return <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${map[s] || "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>{status || "—"}</span>;
};

const StatusBadge = ({ status }) => {
    const s = (status || "pending").toLowerCase();
    const map = {
        verified: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2, label: "Verified" },
        failed: { cls: "bg-red-50 text-red-700 border-red-200", Icon: AlertTriangle, label: "Failed" },
        error: { cls: "bg-red-50 text-red-700 border-red-200", Icon: AlertTriangle, label: "Needs re-auth" },
        pending: { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock, label: "Pending" },
    };
    const { cls, Icon, label } = map[s] || map.pending;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] uppercase tracking-wider border ${cls}`}>
            <Icon className="h-3 w-3" /> {label}
        </span>
    );
};

export default function DataSync() {
    const [conns, setConns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState({});
    const [syncingAll, setSyncingAll] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [history, setHistory] = useState({});
    const [modal, setModal] = useState(null); // { conn, mode: 'sync'|'backfill'|'auto' }
    const [form, setForm] = useState({ days: 30, from: "", interval: 240, lookback: 30 });
    const [saving, setSaving] = useState(false);

    const isoDaysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
    const todayIso = () => new Date().toISOString().slice(0, 10);

    const openModal = (conn, mode) => {
        if (mode === "sync") setForm({ days: conn.sync_lookback_days || 30, from: "", interval: 240, lookback: 30 });
        else if (mode === "backfill") setForm({ days: 30, from: isoDaysAgo(90), interval: 240, lookback: 30 });
        else setForm({ days: 30, from: "", interval: conn.sync_interval_minutes || 240, lookback: conn.sync_lookback_days || 30 });
        setModal({ conn, mode });
    };

    const submitModal = async () => {
        const { conn, mode } = modal;
        setSaving(true);
        try {
            if (mode === "sync") {
                const n = Math.max(1, parseInt(form.days, 10) || 30);
                await api.post(`/platform-connections/${conn.id}/sync?days=${n}`);
                toast.success(`Sync started for ${conn.display_name} (last ${n} days)`);
            } else if (mode === "backfill") {
                if (!form.from) { toast.error("Pick a start date"); setSaving(false); return; }
                const n = Math.max(1, Math.ceil((Date.now() - new Date(form.from).getTime()) / 86400000) + 1);
                await api.post(`/platform-connections/${conn.id}/sync?days=${n}&backfill=true`);
                toast.success(`Back fill started for ${conn.display_name} (${form.from} → today)`);
            } else {
                const interval = Math.max(15, parseInt(form.interval, 10) || 240);
                const lookback = Math.max(1, parseInt(form.lookback, 10) || 30);
                await api.put(`/platform-connections/${conn.id}/sync-interval`, { sync_interval_minutes: interval, sync_lookback_days: lookback });
                toast.success(`Auto-sync updated for ${conn.display_name}`);
            }
            setModal(null);
            setTimeout(load, 2500);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        } finally {
            setSaving(false);
        }
    };

    const load = useCallback(async () => {
        try {
            const r = await api.get("/platform-connections");
            setConns(r.data || []);
        } catch (e) {
            void e;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 15000); // auto-refresh last-synced timestamps
        return () => clearInterval(t);
    }, [load]);

    const syncAll = async () => {
        setSyncingAll(true);
        let ok = 0;
        for (const c of conns) {
            try { await api.post(`/platform-connections/${c.id}/sync`); ok += 1; } catch (e) { void e; }
        }
        toast.success(`Sync triggered for ${ok}/${conns.length} connections`);
        setSyncingAll(false);
        setTimeout(load, 3000);
    };

    const toggleHistory = async (conn) => {
        if (expanded === conn.id) { setExpanded(null); return; }
        setExpanded(conn.id);
        if (!history[conn.id]) {
            try {
                const r = await api.get(`/platform-connections/${conn.id}/sync-jobs`);
                setHistory((h) => ({ ...h, [conn.id]: r.data || [] }));
            } catch (e) {
                setHistory((h) => ({ ...h, [conn.id]: [] }));
            }
        }
    };

    const sorted = [...conns].sort((a, b) => PLATFORM_LABEL(a.platform).localeCompare(PLATFORM_LABEL(b.platform)));
    const overdueCount = sorted.filter(isOverdue).length;

    return (
        <div className="p-8 max-w-6xl" data-testid="data-sync-page">
            <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                    <div className="overline text-zinc-400">Super Admin</div>
                    <h1 className="font-display text-3xl text-zinc-900">Data Sync</h1>
                    <p className="text-sm text-zinc-500 mt-1">
                        Detailed-report sync status for every connected platform — last sync time, the automatic
                        cadence, and a manual trigger.
                    </p>
                </div>
                <button
                    onClick={syncAll}
                    disabled={syncingAll || conns.length === 0}
                    className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="sync-all-btn"
                >
                    <RefreshCw className={`h-4 w-4 ${syncingAll ? "animate-spin" : ""}`} />
                    {syncingAll ? "Triggering…" : "Sync all connections"}
                </button>
            </div>

            {overdueCount > 0 && (
                <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm" data-testid="overdue-banner">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span><strong>{overdueCount}</strong> connection{overdueCount > 1 ? "s haven't" : " hasn't"} synced within 2× its interval — data may be stale. Hit "Sync now" to refresh.</span>
                </div>
            )}

            <div className="border border-zinc-200 rounded-md overflow-hidden bg-white">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-zinc-100 border-b border-zinc-200 text-left">
                            <th className="overline p-3">Platform</th>
                            <th className="overline p-3">Connection</th>
                            <th className="overline p-3">Status</th>
                            <th className="overline p-3">Last Synced</th>
                            <th className="overline p-3">Auto-sync</th>
                            <th className="overline p-3 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="p-10 text-center text-zinc-500">Loading connections…</td></tr>
                        ) : sorted.length === 0 ? (
                            <tr><td colSpan={6} className="p-10 text-center text-zinc-500">
                                <PlugZap className="h-6 w-6 mx-auto mb-2 text-zinc-400" />
                                No platform connections yet. Connect Meta, DV360 or TikTok from Ad Accounts.
                            </td></tr>
                        ) : (
                            sorted.map((c) => {
                                const overdue = isOverdue(c);
                                return (
                                <Fragment key={c.id}>
                                <tr className={`border-b border-zinc-100 hover:bg-zinc-50 ${overdue ? "bg-amber-50/40" : ""}`} data-testid={`sync-row-${c.id}`}>
                                    <td className="p-3 font-medium text-zinc-900">
                                        <button onClick={() => toggleHistory(c)} className="inline-flex items-center gap-1.5 hover:text-black" data-testid={`toggle-history-${c.id}`}>
                                            {expanded === c.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                            {PLATFORM_LABEL(c.platform)}
                                        </button>
                                    </td>
                                    <td className="p-3 text-zinc-700">{c.display_name}</td>
                                    <td className="p-3"><StatusBadge status={c.connection_status} /></td>
                                    <td className="p-3 text-zinc-600 tabular-nums" data-testid={`last-synced-${c.id}`}>
                                        {fmtWhen(c.last_synced_at)}
                                        {overdue && (
                                            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border border-amber-300 bg-amber-100 text-amber-700" data-testid={`overdue-${c.id}`}>
                                                <AlertTriangle className="h-2.5 w-2.5" /> Overdue
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-3 text-zinc-600">{fmtInterval(c.sync_interval_minutes)} · {c.sync_lookback_days || 30}d</td>
                                    <td className="p-3">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <button
                                                onClick={() => openModal(c, "backfill")}
                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs border border-zinc-300 hover:bg-zinc-100"
                                                data-testid={`backfill-${c.id}`}
                                                title="Pull historical data from a chosen start date"
                                            >
                                                <History className="h-3.5 w-3.5" /> Back Fill
                                            </button>
                                            <button
                                                onClick={() => openModal(c, "auto")}
                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs border border-zinc-300 hover:bg-zinc-100"
                                                data-testid={`auto-sync-${c.id}`}
                                                title="Set automatic sync interval and lookback window"
                                            >
                                                <Clock className="h-3.5 w-3.5" /> Auto Sync
                                            </button>
                                            <button
                                                onClick={() => openModal(c, "sync")}
                                                disabled={!!syncing[c.id]}
                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed wl-accent-text"
                                                data-testid={`sync-now-${c.id}`}
                                            >
                                                <RefreshCw className={`h-3.5 w-3.5 ${syncing[c.id] ? "animate-spin" : ""}`} /> Sync Now
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                {expanded === c.id && (
                                    <tr className="bg-zinc-50/70 border-b border-zinc-100" data-testid={`history-${c.id}`}>
                                        <td colSpan={6} className="p-4">
                                            <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-2">Recent sync runs</div>
                                            {!history[c.id] ? (
                                                <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
                                            ) : history[c.id].length === 0 ? (
                                                <div className="text-sm text-zinc-500">No sync runs recorded yet.</div>
                                            ) : (
                                                <table className="w-full text-xs">
                                                    <thead><tr className="text-left text-zinc-400">
                                                        <th className="py-1 pr-4 font-normal">Status</th>
                                                        <th className="py-1 pr-4 font-normal">Started</th>
                                                        <th className="py-1 pr-4 font-normal">Duration</th>
                                                        <th className="py-1 pr-4 font-normal">Rows</th>
                                                        <th className="py-1 pr-4 font-normal">Detail</th>
                                                    </tr></thead>
                                                    <tbody>
                                                        {history[c.id].map((j) => (
                                                            <tr key={j.id} className="border-t border-zinc-100">
                                                                <td className="py-1.5 pr-4"><JobStatusBadge status={j.status} /></td>
                                                                <td className="py-1.5 pr-4 text-zinc-600 tabular-nums">{j.started_at ? (parseUtc(j.started_at)?.toLocaleString("en-US") || "—") : "—"}</td>
                                                                <td className="py-1.5 pr-4 text-zinc-600 tabular-nums">{fmtDur(j.started_at, j.finished_at)}</td>
                                                                <td className="py-1.5 pr-4 text-zinc-600 tabular-nums">{(j.metric_rows || 0).toLocaleString("en-US")}</td>
                                                                <td className="py-1.5 pr-4 text-zinc-500 max-w-md truncate" title={j.error || j.current_step}>{j.error ? <span className="text-red-600">{j.error}</span> : j.current_step}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </td>
                                    </tr>
                                )}
                                </Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <p className="text-xs text-zinc-400 mt-3">
                Timestamps refresh automatically every 15 seconds. "Sync now" queues a fresh pull for that
                connection; detailed-report tables update once the job finishes.
            </p>

            {modal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !saving && setModal(null)} data-testid="sync-modal-overlay">
                    <div className="w-full max-w-md bg-white rounded-lg shadow-xl border border-zinc-200" onClick={(e) => e.stopPropagation()} data-testid="sync-modal">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
                            <h3 className="font-display text-lg text-zinc-900">
                                {modal.mode === "sync" ? "Sync Now" : modal.mode === "backfill" ? "Back Fill" : "Auto Sync"}
                                <span className="ml-2 text-sm text-zinc-400">{modal.conn.display_name}</span>
                            </h3>
                            <button onClick={() => !saving && setModal(null)} className="text-zinc-400 hover:text-zinc-700" data-testid="sync-modal-close"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            {modal.mode === "sync" && (
                                <label className="block">
                                    <span className="text-sm text-zinc-700">Sync the last</span>
                                    <div className="mt-1 flex items-center gap-2">
                                        <input type="number" min="1" max="90" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} className="w-24 px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="sync-days-input" />
                                        <span className="text-sm text-zinc-500">days (up to 90)</span>
                                    </div>
                                </label>
                            )}
                            {modal.mode === "backfill" && (
                                <label className="block">
                                    <span className="text-sm text-zinc-700">Pull data from</span>
                                    <div className="mt-1 flex items-center gap-2">
                                        <input type="date" max={todayIso()} value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} className="px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="backfill-from-input" />
                                        <span className="text-sm text-zinc-500">→ today</span>
                                    </div>
                                    <span className="block mt-1.5 text-xs text-zinc-400">Defaults to 90 days ago. Use this to fill historical data (impressions, spend, True Views).</span>
                                </label>
                            )}
                            {modal.mode === "auto" && (
                                <>
                                    <label className="block">
                                        <span className="text-sm text-zinc-700">Sync every</span>
                                        <select value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value })} className="mt-1 block w-full px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="auto-interval-select">
                                            <option value={60}>1 hour</option>
                                            <option value={120}>2 hours</option>
                                            <option value={240}>4 hours</option>
                                            <option value={480}>8 hours</option>
                                            <option value={720}>12 hours</option>
                                            <option value={1440}>1 day</option>
                                        </select>
                                    </label>
                                    <label className="block">
                                        <span className="text-sm text-zinc-700">Each run pulls the last</span>
                                        <div className="mt-1 flex items-center gap-2">
                                            <input type="number" min="1" max="400" value={form.lookback} onChange={(e) => setForm({ ...form, lookback: e.target.value })} className="w-24 px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="auto-lookback-input" />
                                            <span className="text-sm text-zinc-500">days</span>
                                        </div>
                                    </label>
                                </>
                            )}
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-4 border-t border-zinc-100">
                            <button onClick={() => setModal(null)} disabled={saving} className="px-4 py-2 rounded text-sm border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50" data-testid="sync-modal-cancel">Cancel</button>
                            <button onClick={submitModal} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50" data-testid="sync-modal-submit">
                                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                {modal.mode === "auto" ? "Save" : modal.mode === "backfill" ? "Start Back Fill" : "Sync Now"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
